import type {
	GraphQLResponse,
	Observable,
	RequestParameters,
	Variables,
	CacheConfig,
	GraphQLSingularResponse,
} from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import HttpError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fetch as platformMultiFetch } from '@atlassian/relay-fetch/multi-fetch';
import { withErrorHandling } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import { captureErrors, isUnauthenticated } from '@atlassian/jira-relay-errors/src/index.tsx';
import { getQueryResponseCache } from '@atlassian/jira-relay-query-responses/src/index.tsx';
import {
	claimEarlyScriptPromise,
	getEarlyScriptPromiseKey,
} from '@atlassian/jira-relay-vendorless-promise/src/index.tsx';

import { fetch as multiFetch } from './multi-fetch.tsx';
import { showUnauthenticatedFlag } from './services/error-flags/index.tsx';
import { getLoggingAndMetricsHelper } from './services/logging-and-metrics/index.tsx';
import { fetch as singleFetch } from './single-fetch.tsx';
import { blocklist, generateOptions } from './utils.tsx';

export const fetch = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
): Observable<GraphQLResponse> => {
	if (fg('platform_relay_use_new_multifetch')) {
		const dependencies = {
			withErrorHandling,
			getLoggingAndMetricsHelper,
			setEndpoint: getReroutableURL,
			validateResponse,
			onInvalidResponse,
			setOptions,
			earlyScriptPromise,
			handleAbort,
			queryResponseCache: getQueryResponseCache(),
			isServer: __SERVER__,
		};
		return platformMultiFetch(request, variables, cacheConfig, dependencies);
	}

	return fg('jfp-magma-support-defer')
		? multiFetch(request, variables, cacheConfig)
		: singleFetch(request, variables, cacheConfig);
};

function validateResponse(request: RequestParameters, response: GraphQLSingularResponse): boolean {
	const errors = 'errors' in response ? response.errors : undefined;
	if (errors != null) {
		const requestId = response?.extensions?.gateway?.request_id;
		if (!blocklist.includes(request.name)) {
			getLoggingAndMetricsHelper({
				queryName: request.name,
				operationKind: request.operationKind,
			}).logNonBreakingErrors(errors, requestId);
		}
		// @ts-expect-error -- AGGError.path has a type of `string[]`, whereas PayloadError.path has a type of `(string | number)[]`

		captureErrors(errors, { operationName: request.name, requestId });

		// @ts-expect-error -- AGGError.path has a type of `string[]`, whereas PayloadError.path has a type of `(string | number)[]`
		if (isUnauthenticated(errors)) {
			return false;
		}
	}
	return true;
}

function onInvalidResponse(request: RequestParameters, _response: GraphQLSingularResponse) {
	//  Short circuit operation and handle the unauthorized errors.
	showUnauthenticatedFlag(request.name);
}

function setOptions(endpoint: string, options: RequestInit): RequestInit {
	return generateOptions(options, endpoint);
}

const earlyScriptPromise = (request: RequestParameters, variables: Variables) =>
	(!__SERVER__ && claimEarlyScriptPromise(getEarlyScriptPromiseKey(request, variables))) ||
	undefined;

const handleAbort = (request: RequestParameters, reason: string) => {
	// We didn't receive everything!
	const err = new HttpError(424, `Incomplete handover for relay fetch: ${reason}`);
	err.name = 'ServerToClientError';
	getLoggingAndMetricsHelper({
		queryName: request.name,
		operationKind: request.operationKind,
	}).logError(err);
};
